import type { FormField } from "@tellescope/types-models";

/**
 * The ids of a Question Group's sub-fields, or `[]` for any other field type.
 *
 * Sub-fields are rendered inside their parent group rather than as their own
 * page, so callers use this to fold a group's members into the set of fields
 * they treat as visited/included alongside the group itself.
 */
export const subFieldIdsOf = (field: FormField): string[] =>
  field.type === "Question Group" ? (field.options?.subFields ?? []).map((sf) => sf.id) : [];

/**
 * Whether a field carries no UI of its own and should be stepped over during navigation.
 *
 * True for `Hidden Value` fields, and for a Question Group whose sub-fields are
 * *all* `Hidden Value` — such a group renders nothing, so stopping on it would
 * strand the wizard on a blank page. An empty group is not pass-through.
 *
 * @param fieldsMap all fields by id, used to resolve a group's sub-field types
 */
export const isPassThroughField = (field: FormField, fieldsMap: Record<string, FormField | undefined>): boolean => {
  if (field.type === "Hidden Value") return true;
  if (field.type !== "Question Group") return false;
  const subFields = field.options?.subFields ?? [];
  if (subFields.length === 0) return false;
  return subFields.every((sf) => fieldsMap[sf.id]?.type === "Hidden Value");
};

/**
 * Whether a field occupies a page of its own in the wizard.
 *
 * The single definition of "a page" shared by navigation, the back stack, and the
 * page count, so a progress percentage can never exceed 100%. Sub-fields render
 * inside their parent group and pass-through fields render nothing, so neither
 * gets a page.
 *
 * @param subFieldIds ids belonging to a Question Group
 * @param fieldsMap all fields by id, used to resolve a group's sub-field types
 */
export const occupiesOwnPage = (
  field: FormField,
  subFieldIds: ReadonlySet<string>,
  fieldsMap: Record<string, FormField | undefined>
): boolean => !subFieldIds.has(field.id) && !isPassThroughField(field, fieldsMap);

/**
 * Whether every sub-field of a Question Group already has an answer.
 *
 * A group is only considered answered when it has at least one sub-field and all
 * of them appear in either set, so an empty group never counts as answered.
 *
 * @param answeredFieldIds ids answered this session
 * @param existingResponseFieldIds ids answered by a previously saved response
 */
export const isAnsweredQuestionGroup = (
  field: FormField,
  answeredFieldIds: ReadonlySet<string>,
  existingResponseFieldIds: ReadonlySet<string>
): boolean => {
  if (field.type !== "Question Group") return false;
  const subFields = field.options?.subFields ?? [];
  if (subFields.length === 0) return false;
  return subFields.every((sf) => answeredFieldIds.has(sf.id) || existingResponseFieldIds.has(sf.id));
};
